import { useMemo } from 'react';
import {
  restaurantCategoryIdsOf,
  useRestaurantCategoryNames,
} from '@/entities/restaurant-category';
import type { ModerationEntity, Translate } from '@/entities/moderation';

interface RestaurantCategoriesSectionProps {
  own: ModerationEntity;
  other: ModerationEntity | null | undefined;
  highlight: boolean;
  t: Translate;
}

/**
 * Branch rubrics for the restaurant compare columns — resolved names instead
 * of the raw `restaurant_category_ids` id dump (covered here, so
 * `ChangedFieldsSection` skips that key). The pending side highlights in
 * brand orange when the assigned set differs from the live one.
 */
export function RestaurantCategoriesSection({
  own,
  other,
  highlight,
  t,
}: RestaurantCategoriesSectionProps) {
  const ownIds = useMemo(() => restaurantCategoryIdsOf(own), [own]);
  const otherIds = useMemo(() => restaurantCategoryIdsOf(other ?? null), [other]);
  const { names } = useRestaurantCategoryNames(ownIds);
  const changed = !sameIdSet(ownIds, otherIds);

  return (
    <div>
      <h4 className="text-[14px] leading-[normal] font-semibold tracking-[-0.28px] text-black dark:text-foreground">
        {t('detail.categories', 'Категории')}
      </h4>
      <p
        className={
          highlight && changed
            ? 'mt-3 text-sm leading-[1.4] font-normal break-words text-destructive'
            : 'mt-3 text-sm leading-[1.4] font-normal break-words text-foreground'
        }
      >
        {ownIds.length === 0 ? '—' : names.join(', ')}
      </p>
    </div>
  );
}

/** Order-insensitive id-set equality (both sides are already deduped). */
function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, index) => id === sortedB[index]);
}
